import React, { useMemo, useState } from "react";
import { NodeProps, Position } from "@xyflow/react";
import classNames from "classnames";
import { RiNodeTree } from "@remixicon/react";
import { useDndContext, useDraggable } from "@dnd-kit/core";
import {
    WorkflowStep,
} from "../../../../__generated__/api";
import BaseHandle from "../BaseHandle";
import Styles from "./Nodes.module.scss";
import Avatar from "../../../avatar/Avatar";
import { ACTION_ICON_MAP } from "../../workflow/node-mappings";
import { useWorkflow, WorkflowStepWithParentId } from "../../providers/WorkflowProvider";
import Dropdown from "../../../theme/dropdown/Dropdown";
import { useWorkflowSidebar } from "../../providers/WorkflowSidebarProvider";
import Icon from "../../../theme/icon/Icon";
import Portal from "../../../portal/Portal";
import { SidebarContent, useWorkflowModal } from "../../providers/WorkflowModalProvider";

export interface ActionNodeButtonProps {
    action: WorkflowStep & { label?: string };
    shouldDisableSettings?: boolean;
    handleDeleteNode?: (event: React.MouseEvent) => void;
    handleActionClick?: () => void;
    stopPropagation?: () => void;
}

export function ActionNodeButton({
    action, shouldDisableSettings, handleDeleteNode, handleActionClick, stopPropagation,
}: ActionNodeButtonProps) {
    const { active } = useDndContext();
    const [showSettings, setShowSettings] = useState(false);

    const icon = useMemo(() => {
        if (action.service) {
            // setting className to "" to override the default className
            return <Avatar className="" src={action.service.squareIconUrl ?? ""} variant="xxs" />;
        }

        const ActionIcon = ACTION_ICON_MAP[action.actionId as keyof typeof ACTION_ICON_MAP];

        const IconComponent = ActionIcon ?? RiNodeTree;

        return <Icon size="medium" icon={<IconComponent className={Styles["action-node-icon"]} />} />;
    }, [action.service, action.actionId]);

    const title = useMemo(() => action.action?.name, [action]);
    const name = useMemo(() => action.name ?? action.label, [action]);

    const toggleSettings = () => {
        setShowSettings(!showSettings);
    };
    return (
        <div className={Styles["node-container"]}>
            <button
                type="button"
                className={classNames(Styles["base-action-node"], Styles["draggable-node-wrapper"], {
                    [Styles["dragging-node"]]: active?.id === action.id && !shouldDisableSettings,
                })}
                onClick={handleActionClick}
            >
                {icon}
                <div className={Styles["vertical-line"]} />
                <div>
                    <span className={classNames(Styles["overflow-ellipsis"], Styles["node-title"])}>{title}</span>
                    <p className={classNames(Styles["overflow-ellipsis"], Styles["node-description"])}>{name}</p>
                </div>

            </button>
            {
                !shouldDisableSettings && (
                    <div className={Styles["dropdown-container"]}>
                        <Dropdown show={showSettings} onToggle={toggleSettings} onClick={stopPropagation}>
                            <Dropdown.Toggle
                                variant="none"
                                size="small"
                                onClick={toggleSettings}
                                prefixIcon={(
                                    <Icon.More
                                        variant="default"
                                        size="small"
                                        className={Styles["options-icon"]}
                                        isRounded
                                    />
                                )}
                            />
                            <Portal>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleDeleteNode}>
                                        Delete
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Portal>
                        </Dropdown>
                    </div>
                )
            }
        </div>
    );
}

ActionNodeButton.defaultProps = {
    shouldDisableSettings: false,
    handleDeleteNode: undefined,
    handleActionClick: undefined,
    stopPropagation: undefined,
};

export default function BaseActionNode(props: NodeProps & { data: WorkflowStep & { index?: number } }) {
    const { id, data: step } = props;

    const { attributes, listeners, setNodeRef } = useDraggable({
        id,
        data: props,
    });

    const {
        setTemporaryNodeId, setSelectedNode, deleteNodeFromWorkflow,
    } = useWorkflow();
    const { resetTemporaryState } = useWorkflowSidebar();

    const { setIsSidebarOpen, setSidebarContent } = useWorkflowModal();

    const handleDeleteNode = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        deleteNodeFromWorkflow({ nodeId: id });
    };

    const handleActionClick = () => {
        resetTemporaryState();
        setTemporaryNodeId(id);
        setSelectedNode(step as WorkflowStepWithParentId);
        setIsSidebarOpen(true);
        setSidebarContent(SidebarContent.ACTIONS);
    };

    return (
        <>
            <BaseHandle type="target" position={Position.Left} id="a" />
            <div
                ref={setNodeRef}
                {...listeners}
                {...attributes}
            >
                <ActionNodeButton
                    handleDeleteNode={handleDeleteNode}
                    handleActionClick={handleActionClick}
                    action={step}
                />
            </div>
            <BaseHandle type="source" position={Position.Right} id="b" />
        </>
    );
}
